import { AGraphMandateRestriction } from '../AWPortfolioManagement';

export interface AGraphCheckRestrictionsPayload {
  assetIdentifiers?: {
    values: Array<string>;
  };
  assetListTags?: {
    values: Array<string>;
  };
  metadata?: {
    [key: string]: {
      numberValue?: number;
      stringValue?: string;
    };
  };
  pkg: string;
  platformId: string;
}

export interface ProspectusOutcome {
  contributorRestrictionOutcomeCode?: string;
  description?: string;
  metadata?: {
    [key: string]: {
      numberValue?: number;
      stringValue?: string;
    };
  };
}

export interface RestrictionContributor {
  prospectusOutcomes?: Array<ProspectusOutcome>;
  restrictionScopeValue?: string;
  ruleOutcomes: Array<RuleOutcome>;
}

export interface RuleOutcome {
  contributorRestrictionOutcomeCode?: string;
  description?: string;
  metadata?: {
    [key: string]: {
      numberValue?: number;
      stringValue?: string;
    };
  };
}
export interface AGraphCheckRestrictionsResponse {
  missingAssetIds?: Array<string>;
  restrictionContributors?: Array<RestrictionContributor>;
  restrictions?: Array<AGraphMandateRestriction>;
}

export interface AGraphDocumentRequest {
  documentIdentifier: string;
  documentRequestType: DocumentRequestType;
  metadata?: {
    [key: string]: {
      numberValue?: number;
      stringValue?: string;
    };
  };
}

export interface AGraphDocumentLinksBySecuritiesPayload {
  documentRequests: AGraphDocumentRequest[];
  metadata?: {
    [key: string]: {
      numberValue?: number;
      stringValue?: string;
    };
  };
  pkg: string;
  platformId: string;
}

export enum DocumentRequestType {
  BATCH_GET_SECURITY_DOCUMENTS = 'BATCH_GET_SECURITY_DOCUMENTS',
}

export enum DocumentRequirementStatus {
  DOCUMENT_REQUIREMENT_STATUS_OPTIONAL = 'DOCUMENT_REQUIREMENT_STATUS_OPTIONAL',
  DOCUMENT_REQUIREMENT_STATUS_REQUIRED = 'DOCUMENT_REQUIREMENT_STATUS_REQUIRED',
  DOCUMENT_REQUIREMENT_STATUS_UNSPECIFIED = 'DOCUMENT_REQUIREMENT_STATUS_UNSPECIFIED',
}
export interface DocumentLink {
  createTime: string | null;
  languageCode: string;
  updateTime: string | null;
  uri: string;
}

// Interface for a single document
export interface ReportDocument {
  documentLinks: DocumentLink[];
  documentType: string;
  id: string;
  metadata?: {
    [key: string]: {
      numberValue?: number;
      stringValue?: string;
    };
  };
  requirementStatus: DocumentRequirementStatus;
}

// Interface for auto attachments
export interface AttachmentsMetadata {
  attested: boolean;
  documentErrors: {
    securityId: string;
    documentTypeName: string;
  }[];
  timestamp: string;
}

// Interface for the DocumentLinksBySecurities object
export interface DocumentLinksBySecurities {
  documentIdentifier: string; // security ID
  documents: ReportDocument[];
  metadata: {
    [key: string]: {
      numberValue?: number;
      stringValue?: string;
    };
  };
}

export interface DocumentLinksBySecuritiesError {
  code: number;
  details?: {
    '@type': string;
    value?: string;
  }[];
  message: string;
}

// Interface for the AGraphDocumentLinksBySecuritiesResponse object
export interface AGraphDocumentLinksBySecuritiesResponse {
  results: {
    output?: DocumentLinksBySecurities;
    error?: DocumentLinksBySecuritiesError;
  }[];
}

export type AWOutboundMetadata = { stringValue?: string; numberValue?: number };

// Interface for outbound operation request object
export interface AWOutboundOperation {
  metadata?: Record<string, AWOutboundMetadata>;
  payload?: object;
  payloadType: string;
  pkg: string;
}

export interface AWOutboundErrorResponse {
  code: string;
  message: string;
}

export interface AWOutboundOperationResponse {
  payload: object;
}

export enum Direction {
  BUY = 'BUY',
  SELL = 'SELL',
}

export interface AssetIdentifiers {
  clientId: string;
  cusip?: string;
  isin?: string;
  ric?: string;
  sedol?: string;
}

export interface ExecutionOrder {
  direction: Direction;
  identifiers: AssetIdentifiers;
  marketValue: number;
  metadata: Record<string, unknown>;
  orderId: string;
  portfolioId: string;
  positionId: string;
  price?: number;
  quantity: number;
  status: string;
}

export interface OrderDetails {
  executionOrders: ExecutionOrder[];
  id: string;
}

export interface MetadataValueConfig {
  numberValue?: number;
  stringValue?: string;
}

// EntityTypes are used for outbound operations, they are included in the metadata attributes.
export enum EntityTypes {
  ACCOUNTS = 'accounts', // Documents, Financial Accounts
  BENCHMARK = 'benchmark',
  DECODES = 'decodes',
  ENROLLMENT = 'enrollment',
  FACTOR_TILT = 'factortilt',
  RESTRICTION = 'restriction',
  STRATEGY = 'strategy',
  USERS = 'users', // Shared with contact
}

export interface NewsArticle {
  body?: string;
  headline: string;
  id?: string;
}

export interface HistoricalReportsResponse {
  id: string;
  metadata: Record<string, unknown>;
  name: string;
}

export interface HistoricalReportsPayload {
  accounts: Array<{ tag: string; value: string }>;
  metadata: {
    [key: string]: string;
  };
}

export interface HistoricalReportsPayloadResponse {
  historicalReports: HistoricalReportsResponse[];
}
